/**
 * SportsMoneyPage — the owner's money pool, in one place.
 *
 * Everything is read from the sports_payments ledger:
 *   - Cashboxes: the facility drawer (cashier) + one box per event
 *     (tournament / open play). Boxes appear the moment their first peso lands.
 *   - Collected by method and by person (staff, marshals, online).
 *   - Every ledger entry for the range, newest first.
 *   - Event boxes drill into the full settlement (gross / comped / collected /
 *     outstanding by name / per collector).
 */
import { useState, useEffect, useCallback } from 'react';
import { Wallet, RefreshCw } from 'lucide-react';
import { sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import { ymdTz } from '../utils/sportsUtils';

const peso = (v) => `₱${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const METHOD_COLORS = {
  cash: '#00E676', gcash: '#2979FF', card: '#8B5CF6',
  paymongo: '#FFB300', bank: '#26C6DA', free: '#737373',
};

export default function SportsMoneyPage() {
  const today = ymdTz('Asia/Manila');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settleBox, setSettleBox] = useState(null);   // cashbox id expanded
  const [settlement, setSettlement] = useState(null); // fetched settlement for settleBox

  const load = useCallback(async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      const r = await sportsApi.get('/sports/reports/collections',
        { params: { date_from: from, date_to: to } });
      setRep(r.data);
    } catch { setRep(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const preset = (days) => {
    const to = today;
    const from = days === 0 ? today
      : new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);
    setDateFrom(from); setDateTo(to); setSettleBox(null); setSettlement(null);
    load(from, to);
  };

  // Cashbox metadata derived from the entries themselves: name + event kind.
  const entries = rep?.entries || [];
  const boxMeta = {};
  for (const e of entries) {
    const box = e.cashbox || 'facility';
    if (!boxMeta[box]) {
      boxMeta[box] = { name: box === 'facility' ? 'Facility drawer (cashier)' : (e.event_name || 'Event'),
                       kind: e.context_kind, event_id: box.startsWith('event:') ? box.slice(6) : null };
    }
  }

  const openSettlement = async (box) => {
    if (settleBox === box) { setSettleBox(null); setSettlement(null); return; }
    setSettleBox(box); setSettlement(null);
    const meta = boxMeta[box];
    if (!meta?.event_id) return;
    const base = meta.kind === 'open_play' ? 'open-play' : 'tournaments';
    try {
      const r = await sportsApi.get(`/sports/${base}/${meta.event_id}/settlement`);
      setSettlement(r.data);
    } catch { setSettlement({ error: true }); }
  };

  const boxes = Object.entries(rep?.by_cashbox || {}).sort((a, b) => b[1].amount - a[1].amount);

  const Section = ({ title, children }) => (
    <div className="rounded-xl p-4 mb-4" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
      <p className="text-xs uppercase tracking-widest mb-3" style={{ color: '#737373', fontFamily: 'Oswald, sans-serif' }}>{title}</p>
      {children}
    </div>
  );

  const AggList = ({ agg, colorFor }) => (
    <div>
      {Object.entries(agg || {}).sort((a, b) => b[1].amount - a[1].amount).map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #191919' }}>
          <p className="text-sm" style={{ color: colorFor ? (colorFor(k) || '#A3A3A3') : '#A3A3A3' }}>
            {k.replace('organizer:', '👤 ').replace('online', '🌐 online')}
          </p>
          <p className="text-sm font-bold" style={{ color: '#E5E5E5' }}>{peso(v.amount)} <span style={{ color: '#555', fontWeight: 400 }}>({v.count})</span></p>
        </div>
      ))}
      {Object.keys(agg || {}).length === 0 && <p className="text-xs" style={{ color: '#555' }}>— nothing in this range —</p>}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />
      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-3xl uppercase tracking-tight" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>
              <Wallet className="inline h-7 w-7 mr-2" style={{ color: '#00E676' }} />Money
            </h1>
            <p className="text-xs mt-1" style={{ color: '#737373' }}>
              Every peso in the system — facility drawer, event cashboxes, who collected what.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[['Today', 0], ['7 days', 7], ['30 days', 30]].map(([l, d]) => (
              <button key={l} onClick={() => preset(d)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase"
                style={{ background: '#141414', border: '1px solid #2A2A2A', color: '#A3A3A3', cursor: 'pointer' }}>
                {l}
              </button>
            ))}
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ background: '#141414', border: '1px solid #2A2A2A', color: '#A3A3A3', borderRadius: 8, padding: '6px 8px', fontSize: 12 }} />
            <span style={{ color: '#555' }}>–</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ background: '#141414', border: '1px solid #2A2A2A', color: '#A3A3A3', borderRadius: 8, padding: '6px 8px', fontSize: 12 }} />
            <button onClick={() => load()} data-testid="money-load-btn"
              className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase"
              style={{ background: '#00E67618', border: '1px solid #00E67640', color: '#00E676', cursor: 'pointer' }}>
              <RefreshCw className="inline h-3 w-3 mr-1" />Load
            </button>
          </div>
        </div>

        {loading && <p className="text-sm" style={{ color: '#555' }}>Counting…</p>}
        {!loading && rep && (
          <>
            {/* Headline numbers */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
              <div className="rounded-xl p-4" style={{ background: '#0A1A0A', border: '1px solid #00E67630' }}>
                <p className="text-xs uppercase" style={{ color: '#00E676' }}>Collected</p>
                <p className="text-3xl font-black" style={{ color: '#00E676', fontFamily: 'Oswald, sans-serif' }} data-testid="money-total">
                  {peso(rep.total_collected)}
                </p>
              </div>
              <div className="rounded-xl p-4" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
                <p className="text-xs uppercase" style={{ color: '#737373' }}>Entries</p>
                <p className="text-3xl font-black" style={{ fontFamily: 'Oswald, sans-serif' }}>{entries.filter(e => e.type === 'payment').length}</p>
              </div>
              <div className="rounded-xl p-4" style={{ background: '#141414', border: '1px solid #FFB30030' }}>
                <p className="text-xs uppercase" style={{ color: '#FFB300' }}>Waived / comped</p>
                <p className="text-3xl font-black" style={{ color: '#FFB300', fontFamily: 'Oswald, sans-serif' }}>{peso(rep.comps_waived)}</p>
              </div>
            </div>

            {/* Cashboxes */}
            <Section title="Cashboxes — where the money physically sits">
              {boxes.length === 0 && <p className="text-xs" style={{ color: '#555' }}>No money recorded in this range yet — boxes appear with their first peso.</p>}
              {boxes.map(([box, v]) => {
                const meta = boxMeta[box] || {};
                const isEvent = box !== 'facility';
                return (
                  <div key={box} className="mb-2">
                    <div onClick={() => isEvent && openSettlement(box)} data-testid={`money-box-${box}`}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#0A0A0A', border: `1px solid ${settleBox === box ? '#00E67640' : '#1E1E1E'}`, borderRadius: 10, padding: '12px 16px', cursor: isEvent ? 'pointer' : 'default' }}>
                      <div>
                        <p className="text-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>
                          {isEvent ? '🏆 ' : '🏪 '}{meta.name || box}
                        </p>
                        <p className="text-xs" style={{ color: '#555' }}>
                          {isEvent ? `${meta.kind === 'open_play' ? 'Open play' : 'Tournament'} cashbox · tap for settlement` : 'Bookings, rentals, open play at the desk'}
                        </p>
                      </div>
                      <p className="text-lg font-black" style={{ color: '#00E676', fontFamily: 'Oswald, sans-serif' }}>{peso(v.amount)}</p>
                    </div>
                    {settleBox === box && (
                      <div style={{ background: '#0D0D0D', border: '1px solid #1E1E1E', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '12px 16px' }}>
                        {!settlement && <p className="text-xs" style={{ color: '#555' }}>Loading settlement…</p>}
                        {settlement?.error && <p className="text-xs" style={{ color: '#FF2D55' }}>Could not load the settlement.</p>}
                        {settlement && !settlement.error && (
                          <>
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                              {[['Gross', settlement.gross_expected, '#fff'], ['Comped', settlement.comped, '#FFB300'],
                                ['Collectable', settlement.collectable, '#A3A3A3'], ['Collected', settlement.collected, '#00E676'],
                                ['Still owed', settlement.outstanding_total, '#FF2D55']].map(([k, v2, c]) => (
                                <div key={k} style={{ background: '#141414', borderRadius: 8, padding: '8px 10px' }}>
                                  <p style={{ fontSize: 9, textTransform: 'uppercase', color: '#555' }}>{k}</p>
                                  <p className="text-sm font-black" style={{ color: c }}>{peso(v2)}</p>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs mb-2" style={{ color: '#737373' }}>
                              Of the collected: <b style={{ color: '#00E676' }}>{peso(settlement.collected_event_box)}</b> is with the organizer (they owe you this at handover)
                              {settlement.collected_facility > 0 && <> · <b style={{ color: '#2979FF' }}>{peso(settlement.collected_facility)}</b> already in your facility drawer</>}
                            </p>
                            {(settlement.outstanding || []).length > 0 && (
                              <div className="mb-2">
                                <p className="text-xs font-bold uppercase mb-1" style={{ color: '#FF2D55' }}>Still owes</p>
                                {settlement.outstanding.map((o, i) => (
                                  <p key={i} className="text-xs" style={{ color: '#A3A3A3' }}>
                                    {o.who} — <b style={{ color: '#FF2D55' }}>{peso(o.balance)}</b>{o.present ? ' · was present' : ''}
                                  </p>
                                ))}
                              </div>
                            )}
                            {Object.keys(settlement.by_collector || {}).length > 0 && (
                              <div>
                                <p className="text-xs font-bold uppercase mb-1" style={{ color: '#737373' }}>Collected by</p>
                                {Object.entries(settlement.by_collector).map(([who, v2]) => (
                                  <p key={who} className="text-xs" style={{ color: '#A3A3A3' }}>
                                    {who.replace('organizer:', '👤 ')} — <b style={{ color: '#00E676' }}>{peso(v2.amount)}</b> ({v2.count})
                                  </p>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </Section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Section title="Collected — by method">
                <AggList agg={rep.by_method} colorFor={(k) => METHOD_COLORS[k]} />
              </Section>
              <Section title="Collected — by person">
                <AggList agg={rep.by_collector} />
              </Section>
            </div>

            {/* Ledger entries */}
            <Section title={`Ledger — ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'} (newest first)`}>
              <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                {entries.slice().reverse().map(e => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #191919' }}>
                    <div style={{ minWidth: 92, flexShrink: 0 }}>
                      <p className="text-xs" style={{ color: '#555' }}>{(e.created_at || '').slice(5, 16).replace('T', ' ')}</p>
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 800, textTransform: 'uppercase', background: (METHOD_COLORS[e.method] || '#737373') + '20', color: METHOD_COLORS[e.method] || '#737373' }}>
                        {e.type === 'comp' ? 'COMP' : e.method}
                      </span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="text-sm font-semibold" style={{ color: '#ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {e.customer_name || '—'} <span style={{ color: '#555', fontWeight: 400 }}>· {e.event_name || e.context_kind}{e.note ? ` · ${e.note}` : ''}</span>
                      </p>
                      <p className="text-xs" style={{ color: '#555' }}>by {e.recorded_by}{e.tendered != null ? ` · received ${peso(e.tendered)}, change ${peso(e.change)}` : ''}</p>
                    </div>
                    <p className="text-sm font-black" style={{ color: e.type === 'comp' ? '#FFB300' : '#00E676', flexShrink: 0, fontFamily: 'Oswald, sans-serif' }}>
                      {e.type === 'comp' ? `waived ${peso(e.waived_amount)}` : peso(e.amount)}
                    </p>
                  </div>
                ))}
                {entries.length === 0 && <p className="text-xs" style={{ color: '#555' }}>No ledger entries in this range.</p>}
              </div>
            </Section>
          </>
        )}
      </main>
    </div>
  );
}
